import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';

/**
 * Kontextuelle In-Dialog-Tour fuer den "Schichtplan-Analyse"-Dialog
 * (modules/admin/component/shift-plan-analysis). Wird via Hilfe-Button
 * (`<app-dialog-help-button>`) im Hero-Header manuell gestartet. Erklaert in
 * 6 Schritten: Welcome → Gesundheits-Score (Donut + Status-Pill) →
 * Vergleichszeitraum → Tab-Leiste (6 Auswertungen) → Risiko-Score (Rail) →
 * Gesundheit-pro-Tag-Matrix (Overview-Tab, Default-Tab — kein Tab-Switch noetig).
 *
 * Pattern analog zu `AUTO_SUGGEST_HELP_TOUR`: erklaerungs-getrieben (manuelles
 * Weiterklicken, `nextCondition: 'manual'`), nicht formular-gebunden.
 *
 * Fachliche Quelle: `shift-plan-analysis/CLAUDE.md` §6 (Daily-Health-Matrix),
 * §7 (Health-Composite-Gewichtung 30/25/20/15/10), `WarningsAndHints.RiskScore`.
 */
export const SHIFT_PLAN_ANALYSIS_HELP_TOUR: TourConfig = {
  id: TourFeature.ShiftPlanAnalysisHelp,
  version: 1,
  title: 'SHIFT_PLAN_ANALYSIS_HELP_TOUR_TITLE',
  description: 'SHIFT_PLAN_ANALYSIS_HELP_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  // Help-Hub-Start: zur Schichtzuweisung navigieren. Der "Analyse"-Button
  // sitzt seit dem Toolbar-Refactor im Overflow-Menue
  // (`fc-overflowMenuButton-button`) — kein deterministischer 1-Klick-Opener.
  // Daher `route` only; User oeffnet Menue + Item selbst.
  launchContext: {
    route: '/planner',
  },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 75,
  // Bewusst 'feature-discovery' (NICHT 'contextual-help'): wir wollen die
  // klassische Guided Tour mit Spotlight + Voice-Over, nicht den HelpModeService
  // mit (i)-Icons. Begruendung siehe AUTO_SUGGEST_HELP_TOUR.
  category: 'feature-discovery',
  chapters: [
    {
      id: 'overview',
      title: 'SHIFT_PLAN_ANALYSIS_HELP_TOUR_TITLE',
      steps: [
        {
          id: 'welcome',
          // Welcome erklaert den GANZEN Dialog — Spotlight am .spa-Wrapper.
          anchor: AnchorId.ShiftPlanAnalysisDialog,
          title: 'TUTORIAL.SHIFT_PLAN_ANALYSIS.WELCOME.TITLE',
          content: 'TUTORIAL.SHIFT_PLAN_ANALYSIS.WELCOME.CONTENT',
          placement: 'auto',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
        },
        {
          id: 'health-score',
          // Spotlight umfasst Donut UND Status-Pill — beide gehoeren zusammen
          // (Score-Wert ↔ Status-Label). Zwei separate Cutouts via
          // additionalSpotlightAnchors, kein zusaetzlicher Cue noetig.
          anchor: AnchorId.ShiftPlanAnalysisHealthScore,
          additionalSpotlightAnchors: [AnchorId.ShiftPlanAnalysisStatus],
          title: 'TUTORIAL.SHIFT_PLAN_ANALYSIS.HEALTH_SCORE.TITLE',
          content: 'TUTORIAL.SHIFT_PLAN_ANALYSIS.HEALTH_SCORE.CONTENT',
          placement: 'bottom',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
        },
        {
          id: 'compare',
          anchor: AnchorId.ShiftPlanAnalysisCompare,
          title: 'TUTORIAL.SHIFT_PLAN_ANALYSIS.COMPARE.TITLE',
          content: 'TUTORIAL.SHIFT_PLAN_ANALYSIS.COMPARE.CONTENT',
          placement: 'bottom',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
        },
        {
          id: 'tabs',
          anchor: AnchorId.ShiftPlanAnalysisTabs,
          title: 'TUTORIAL.SHIFT_PLAN_ANALYSIS.TABS.TITLE',
          content: 'TUTORIAL.SHIFT_PLAN_ANALYSIS.TABS.CONTENT',
          placement: 'right',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
        },
        {
          id: 'risk',
          anchor: AnchorId.ShiftPlanAnalysisRisk,
          title: 'TUTORIAL.SHIFT_PLAN_ANALYSIS.RISK.TITLE',
          content: 'TUTORIAL.SHIFT_PLAN_ANALYSIS.RISK.CONTENT',
          placement: 'right',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
        },
        {
          id: 'health-matrix',
          anchor: AnchorId.ShiftPlanAnalysisHealthMatrix,
          title: 'TUTORIAL.SHIFT_PLAN_ANALYSIS.HEALTH_MATRIX.TITLE',
          content: 'TUTORIAL.SHIFT_PLAN_ANALYSIS.HEALTH_MATRIX.CONTENT',
          placement: 'top',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          // Letzter Step: kein Zurueck-Button noetig, sauberer Abschluss.
          showPrevButton: false,
        },
      ],
    },
  ],
};
